import { useEffect, useRef, useState } from 'react';

function loadSet(key) {
  try {
    const v = sessionStorage.getItem(key);
    return v ? new Set(JSON.parse(v)) : new Set();
  } catch { return new Set(); }
}

function saveSet(key, set) {
  try { sessionStorage.setItem(key, JSON.stringify([...set])); } catch {}
}

function loadValue(key, def) {
  try {
    const v = sessionStorage.getItem(key);
    return v !== null ? JSON.parse(v) : def;
  } catch { return def; }
}

function saveValue(key, val) {
  try { sessionStorage.setItem(key, JSON.stringify(val)); } catch {}
}

// Persiste un Set en sessionStorage. Gère les changements de clé (tenant/année).
export function usePersistentSet(key) {
  const [state, setState] = useState(() => loadSet(key));
  const prevKey = useRef(key);

  useEffect(() => {
    if (prevKey.current !== key) {
      prevKey.current = key;
      setState(loadSet(key));
    }
  }, [key]);

  function set(updater) {
    setState(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      saveSet(key, next);
      return next;
    });
  }

  return [state, set];
}

// Persiste une valeur scalaire en sessionStorage.
export function usePersistentValue(key, defaultValue) {
  const [state, setState] = useState(() => loadValue(key, defaultValue));
  const prevKey = useRef(key);

  useEffect(() => {
    if (prevKey.current !== key) {
      prevKey.current = key;
      setState(loadValue(key, defaultValue));
    }
  }, [key]); // defaultValue intentionally omitted — only key change triggers reload

  function set(value) {
    saveValue(key, value);
    setState(value);
  }

  return [state, set];
}

// Variante localStorage : PARTAGÉE entre onglets/fenêtres et DURABLE (survit à la
// fermeture). Reprise douce depuis l'ancien sessionStorage (migration transparente).
function loadValueLocal(key, def) {
  try {
    let v = localStorage.getItem(key);
    if (v === null) v = sessionStorage.getItem(key); // ancien stockage → migration
    return v !== null ? JSON.parse(v) : def;
  } catch { return def; }
}
function saveValueLocal(key, val) {
  try { localStorage.setItem(key, JSON.stringify(val)); } catch {}
}
export function usePersistentValueLocal(key, defaultValue) {
  const [state, setState] = useState(() => loadValueLocal(key, defaultValue));
  const prevKey = useRef(key);

  // Persiste tout de suite (migre la valeur reprise de sessionStorage vers localStorage).
  useEffect(() => { saveValueLocal(key, state); /* eslint-disable-next-line */ }, []);

  useEffect(() => {
    if (prevKey.current !== key) {
      prevKey.current = key;
      const v = loadValueLocal(key, defaultValue);
      setState(v);
      saveValueLocal(key, v);
    }
  }, [key]); // defaultValue intentionally omitted

  function set(updater) {
    // Accepte une valeur OU une fonction de mise à jour, comme useState — sinon
    // `set(prev => …)` stockait la fonction elle-même (constat 2026-08-17).
    setState((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      saveValueLocal(key, next);
      return next;
    });
  }

  return [state, set];
}
